import { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertOctagon, MessageSquare, Send, Sparkles, RefreshCw, Bot, User as UserIcon, Copy } from "lucide-react";
import { toast } from "sonner";

const fm = (n) => `$${Number(n || 0).toFixed(2)}`;

const SEVERITY = {
  high: "bg-[#FCE7E9] text-[#B4283A] border-l-[#B4283A]",
  medium: "bg-[#FBF7EC] text-[#7A5B00] border-l-[#D5A02A]",
  low: "bg-[var(--hover)] text-[var(--muted-text)] border-l-[var(--divider)]",
};

export default function AIHub() {
  const [tab, setTab] = useState("chat");
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Intelligence</div>
        <h1 className="font-display text-4xl mt-2">AI insights</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          Talk to your books, or let AI flag transactions that look unusual.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[var(--hover)]">
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="w-4 h-4 mr-2" /> Chat with your books</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="tab-anomalies"><AlertOctagon className="w-4 h-4 mr-2" /> Anomalies</TabsTrigger>
        </TabsList>
        <TabsContent value="chat"><ChatTab /></TabsContent>
        <TabsContent value="anomalies"><AnomaliesTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

// ------- Chat -------
function ChatTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    try { const { data } = await api.get("/ai/chat/sessions"); setSessions(data); }
    catch (err) { console.debug("[AIHub] loadSessions failed:", err?.message); }
  };
  const loadHistory = async (sid) => {
    try {
      const { data } = await api.get("/ai/chat/history", { params: { session_id: sid } });
      setMessages(data);
      setSessionId(sid);
    } catch (err) {
      console.debug("[AIHub] loadHistory failed:", err?.message);
      toast.error("Couldn't load chat history");
    }
  };
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const q = input.trim();
    setMessages((m) => [...m, { role: "user", content: q, created_at: new Date().toISOString() }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, follow_ups: data.follow_ups || [], created_at: new Date().toISOString() }]);
      loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chat failed");
      setMessages((m) => m.filter((x) => x.content !== q));
    } finally {
      setSending(false);
    }
  };

  const newChat = () => { setMessages([]); setSessionId(null); };

  const suggestions = [
    { icon: "💰", label: "Cash & Runway", prompt: "How's my cash runway looking? Which accounts have the biggest balances?" },
    { icon: "📊", label: "Top Expenses", prompt: "What are my top 3 expenses this month, and how do they compare to last?" },
    { icon: "🧾", label: "Open Invoices", prompt: "Which customers still owe me money, and what's overdue?" },
    { icon: "⚠️", label: "Budget Check", prompt: "Am I over budget in any category this month? Suggest one thing to cut." },
    { icon: "📈", label: "Investments", prompt: "How's my investment portfolio performing? Any position notably up or down?" },
    { icon: "🎯", label: "Savings Goals", prompt: "What are my active savings goals and am I on track for each?" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
      {/* Sessions rail */}
      <div className="space-y-2">
        <Button size="sm" variant="outline" data-testid="chat-new-btn" onClick={newChat} className="w-full rounded-full">
          <Sparkles className="w-3.5 h-3.5 mr-2" /> New chat
        </Button>
        <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] pt-2 px-1">Recent</div>
        <div className="space-y-1 max-h-[500px] overflow-y-auto">
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => loadHistory(s.session_id)}
              className={`w-full text-left p-2 rounded text-xs ${sessionId === s.session_id ? "bg-[#F5EAE3]" : "hover:bg-[var(--row-hover)]"}`}
              data-testid={`chat-session-${s.session_id.slice(-6)}`}
            >
              <div className="truncate text-[var(--muted-text)] leading-tight">{s.last_message}</div>
              <div className="text-[10px] text-[var(--muted-text)]/70 mt-0.5">{s.count} msgs</div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat pane */}
      <div className="card-flat flex flex-col h-[70vh]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto pt-6" data-testid="chat-welcome">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F5EAE3] to-[#E9D9CC] flex items-center justify-center mx-auto shadow-sm">
                  <Bot className="w-7 h-7 text-[#E07A5F]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-text)] mt-4">
                  AI Advisor · GPT-powered
                </div>
                <h3 className="font-display text-3xl mt-2">Ask your books anything</h3>
                <p className="text-sm text-[var(--muted-text)] mt-2 max-w-md mx-auto">
                  I have full context on your cash, invoices, bills, expenses, budgets, goals,
                  and investments. Try one of these to get going:
                </p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s.label}
                    data-testid={`chat-prompt-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    onClick={() => { setInput(s.prompt); }}
                    className="group text-left px-4 py-3 rounded-xl border border-[var(--divider)] hover:border-[#E07A5F] hover:bg-[#FFF9F5] transition"
                  >
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--muted-text)] group-hover:text-[#E07A5F]">
                      <span className="text-base">{s.icon}</span>
                      <span>{s.label}</span>
                    </div>
                    <div className="text-sm mt-1 leading-snug">{s.prompt}</div>
                  </button>
                ))}
              </div>
              <div className="mt-6 text-center">
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                  Or type your own question below
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={`msg-${m.created_at || i}-${m.role}`} data-testid={`msg-${m.role}-${i}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-[#F5EAE3] flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#E07A5F]" /></div>}
              <div className={`max-w-[70%] ${m.role === "user" ? "" : "space-y-2"}`}>
                <div className={`p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-[#2F4F4F] text-[#FDFCF8]" : "bg-[var(--hover)]"}`}>
                  {m.content}
                </div>
                {m.role === "assistant" && Array.isArray(m.follow_ups) && m.follow_ups.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid={`chat-followups-${i}`}>
                    {m.follow_ups.map((f, idx) => (
                      <button
                        key={`${i}-${f}`}
                        data-testid={`chat-followup-${i}-${idx}`}
                        onClick={() => setInput(f)}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#E07A5F]/30 bg-[#FFF9F5] hover:bg-[#F5EAE3] transition text-left leading-tight"
                        title="Use this follow-up"
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {m.role === "user" && <div className="w-8 h-8 rounded-full bg-[#2F4F4F] flex items-center justify-center shrink-0"><UserIcon className="w-4 h-4 text-[#FDFCF8]" /></div>}
            </div>
          ))}
          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-[#F5EAE3] flex items-center justify-center"><Bot className="w-4 h-4 text-[#E07A5F]" /></div>
              <div className="bg-[var(--hover)] px-4 py-3 rounded-2xl text-sm italic text-[var(--muted-text)]" data-testid="chat-typing">typing…</div>
            </div>
          )}
        </div>
        <div className="border-t border-[var(--divider)] p-4 flex gap-2">
          <Input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
            placeholder="Ask about your finances…"
            className="h-11 rounded-full border-[var(--divider)]"
            disabled={sending}
          />
          <Button data-testid="chat-send" onClick={send} disabled={sending || !input.trim()} className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 w-11 p-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------- Anomalies -------
function AnomaliesTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/ai/anomalies", { params: { days } }); setData(data); }
    catch (err) { toast.error("Failed to load anomalies"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="text-xs uppercase tracking-widest text-[var(--muted-text)]">Look back</div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-40 h-9" data-testid="anom-days"><SelectValue /></SelectTrigger>
          <SelectContent>{[7, 14, 30, 60, 90].map((d) => <SelectItem key={d} value={String(d)}>{d} days</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="rounded-full"><RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        <div className="flex-1" />
        {data && <div className="text-sm text-[var(--muted-text)]" data-testid="anom-count">{data.count} anomal{data.count === 1 ? "y" : "ies"} found</div>}
      </div>

      {data?.narrative && (
        <div className="card-flat p-5 border-l-4 border-l-[#E07A5F] whitespace-pre-line text-sm leading-relaxed" data-testid="anom-narrative">
          <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-2">Auditor's note</div>
          {data.narrative}
        </div>
      )}

      {loading && !data && <div className="text-sm text-[var(--muted-text)]">Scanning…</div>}
      {data && data.anomalies.length === 0 && (
        <div className="card-flat p-10 text-center text-sm text-[var(--muted-text)]">Nothing unusual. Your books look clean.</div>
      )}
      {data && data.anomalies.length > 0 && (
        <div className="space-y-2">
          {data.anomalies.map((a) => (
            <div key={a.id + a.kind} data-testid={`anom-row-${a.id}`} className={`card-flat p-4 border-l-4 ${SEVERITY[a.severity]}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest">{a.severity}</span>
                <span className="text-sm font-medium capitalize">{a.kind.replace("_", " ")}</span>
                <span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">·</span>
                <span className="text-xs text-[var(--muted-text)]">{a.date}</span>
                <div className="flex-1" />
                <span className="font-mono text-sm">{fm(a.amount)}</span>
              </div>
              <div className="text-xs text-[var(--muted-text)] mt-1">
                <span className="capitalize mr-2">{a.category}</span>
                {a.note && <>— <em>{a.note}</em></>}
              </div>
              <div className="text-xs mt-2 opacity-90">{a.reason}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
